/**
 * AuthProvider - Handles Telegram authentication and JWT token management
 * 
 * This provider:
 * 1. Authenticates users with Telegram init data on app load
 * 2. Retrieves JWT token from the API
 * 3. Stores the token in localStorage (used by ReferralClient via TokenManager)
 * 4. Provides authentication state to child components
 * 5. Automatically refreshes expired tokens on 401 errors via interceptors
 * 6. Supports both Telegram SDK mode and browser mode authentication
 * 
 * The JWT token is automatically used by all API clients in @crypto-trading/common/browser
 */
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useLaunchParams } from '@telegram-apps/sdk-react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { 
  TokenManager, 
  ApiClientManager, 
  createTokenRefreshInterceptors,
  getAuthMode,
  isBrowserAuthenticated,
  clearBrowserAuthUser,
} from '@/utils/api-client';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://crypto-news-processor-production.up.railway.app';

type AuthMode = 'telegram' | 'browser';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  loading: boolean;
  error: string | null;
  authMode: AuthMode;
  logout: () => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
  onBrowserAuthRequired?: () => void;
}

const RETRY_INTERVALS = [5, 15, 30, 60, 60]; // 5 sec, 15 sec, 30 sec, 1m, 1m
const MAX_RETRIES = 5;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, onBrowserAuthRequired }) => {
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const launchParams = useLaunchParams();

  const formatTimer = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return `${minutes}m`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  };

  const authenticateUser = useCallback(async (_forceRefresh = false) => {
    try {
      const authMode = getAuthMode();
      
      // Browser mode: Check if already authenticated via browser login
      if (authMode === 'browser' && isBrowserAuthenticated()) {
        const existingToken = TokenManager.getToken();
        if (existingToken && !_forceRefresh) {
          console.log('[AuthProvider] Browser mode: Using existing JWT token');
          setToken(existingToken);
          setError(null);
          setRetryAttempt(0);
          setCountdown(0);
          setLoading(false);
          return;
        }
      }

      // Get Telegram init data
      const initDataRaw = launchParams.initDataRaw;
      
      if (!initDataRaw) {
        throw new Error('No Telegram init data available');
      }

      console.log('[AuthProvider] Authenticating with the API, mode:', authMode);
      // Authenticate with the API
      const response = await axios.post<string>(
        `${API_BASE_URL}/auth/validate?${initDataRaw}`,
        {
          // Optional: Add wallet address if needed
          // address: walletAddress,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('[AuthProvider] Response:', response.data);

      const jwtToken = response.data;

      if (!jwtToken || typeof jwtToken !== 'string') {
        throw new Error('Invalid token received from server');
      }

      // Store token using TokenManager
      TokenManager.setToken(jwtToken);
      setToken(jwtToken);
      setError(null);
      setRetryAttempt(0);
      setCountdown(0);
    } catch (err) {
      console.error('Authentication error:', err);
      
      const authMode = getAuthMode();
      const errorMessage = err instanceof Error ? err.message : 'Failed to authenticate';
      
      setError(errorMessage);
      
      // In browser mode, if auth fails, we need to show login page
      if (authMode === 'browser') {
        console.log('[AuthProvider] Browser mode auth failed, clearing browser auth');
        clearBrowserAuthUser();
        TokenManager.clearToken();
        // Signal that browser login is required
        if (onBrowserAuthRequired) {
          onBrowserAuthRequired();
        }
        // Throw error so refreshAuth can catch it and handle properly
        throw err;
      }
    } finally {
      setLoading(false);
    }
  }, [launchParams.initDataRaw, onBrowserAuthRequired]);

  const refreshAuth = useCallback(async () => {
    console.log('[AuthProvider] Refreshing auth');
    try {
      await authenticateUser(true);
      // Check if authentication succeeded by verifying token exists
      const token = TokenManager.getToken();
      if (!token) {
        throw new Error('Token refresh failed - no token available');
      }
    } catch (err) {
      // authenticateUser already handles browser mode failures and calls onBrowserAuthRequired
      // But we still need to throw to let the interceptor know refresh failed
      console.error('[AuthProvider] Token refresh failed:', err);
      throw err;
    }
  }, [authenticateUser]);

  const setupApiClientInterceptors = useCallback(() => {
    // Create interceptors for token refresh on 401 errors
    const interceptors = createTokenRefreshInterceptors(refreshAuth);
    
    // Set interceptors for all API clients
    ApiClientManager.setInterceptors(interceptors);
    
    console.log('[AuthProvider] API client interceptors configured for token refresh');
  }, [refreshAuth]);

  useEffect(() => {
    // Set up interceptors for API clients before authentication
    setupApiClientInterceptors();
    // Then authenticate
    authenticateUser();
  }, [authenticateUser, setupApiClientInterceptors]);

  // Handle automatic retry with countdown
  useEffect(() => {
    if (!error || retryAttempt >= MAX_RETRIES) {
      // If retries exhausted in browser mode, show login page
      if (error && retryAttempt >= MAX_RETRIES) {
        const authMode = getAuthMode();
        if (authMode === 'browser' && onBrowserAuthRequired) {
          console.log('[AuthProvider] Max retries exhausted in browser mode, showing login page');
          clearBrowserAuthUser();
          TokenManager.clearToken();
          onBrowserAuthRequired();
        }
      }
      return;
    }

    const intervalSeconds = RETRY_INTERVALS[retryAttempt];
    setCountdown(intervalSeconds);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const retryTimeout = setTimeout(() => {
      clearInterval(countdownInterval);
      setRetryAttempt((prev) => prev + 1);
      authenticateUser(true);
    }, intervalSeconds * 1000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(retryTimeout);
    };
  }, [error, retryAttempt, authenticateUser, onBrowserAuthRequired]);

  const logout = () => {
    TokenManager.clearToken();
    clearBrowserAuthUser(); // Also clear browser auth data if in browser mode
    setToken(null);
    
    // In browser mode, reload the page to show login screen
    if (getAuthMode() === 'browser') {
      window.location.reload();
    }
  };

  const authMode = getAuthMode();

  const value: AuthContextType = useMemo(
    () => ({
      isAuthenticated: !!token,
      token,
      loading,
      error,
      authMode,
      logout,
      refreshAuth,
    }),
    [token, loading, error, authMode]
  );

  if (loading) {
    return (
      <AuthContext.Provider value={value}>
        <div className="min-h-screen bg-primary flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
            <p className="text-text-primary">Authenticating...</p>
          </div>
        </div>
      </AuthContext.Provider>
    );
  }

  if (error) {
    const hasRetriesLeft = retryAttempt < MAX_RETRIES;
    
    return (
      <AuthContext.Provider value={value}>
        <div className="min-h-screen bg-primary flex items-center justify-center p-4">
          <div className="text-center max-w-sm">
            {hasRetriesLeft ? (
              <>
                <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
                <h2 className="text-lg font-bold text-text-primary mb-2">
                  Updating...
                </h2>
                <p className="text-text-secondary text-sm">
                  Retrying in {formatTimer(countdown)}
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-400 text-2xl">⚠️</span>
                </div>
                <h2 className="text-lg font-bold text-text-primary mb-2">
                  Update failed
                </h2>
                <p className="text-text-secondary text-sm mb-4">{error}</p>
                <p className="text-text-secondary text-sm">
                  All retry attempts exhausted. Please restart the miniapp
                </p>
              </>
            )}
          </div>
        </div>
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

